import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../components/ui/drawer';
import { Camera, ImagePlus, X, ArrowLeft, AlertCircle, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { compressListingPhoto } from '../utils/imageCompression';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Cleanser', 'Toner', 'Serum', 'Moisturizer', 'Sunscreen', 'Mask', 'Makeup', 'Hair', 'Body', 'Other'];
const CONDITIONS = ['New/Sealed', 'Swatched only', 'Used 1-3x', '~80% left', '~50% left', '~30% left'];
const SELLING_REASONS = ["It doesn't fit me", "I already have one", "I'm using another product", "Other"];
const SIZE_UNITS = ['ml', 'gr', 'oz', 'pcs', 'sets'];

const PHOTO_SLOTS = [
  { key: 'actual_1', label: 'Photo 1', required: true, type: 'actual' },
  { key: 'actual_2', label: 'Photo 2', required: true, type: 'actual' },
  { key: 'actual_3', label: 'Photo 3', required: true, type: 'actual' },
  { key: 'actual_4', label: 'Photo 4', required: false, type: 'actual' },
  { key: 'reference', label: 'Reference', required: false, type: 'reference' },
];

function PhotoSlot({ slot, url, onUpload, onRemove, uploading, photoError }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDrawerOpen(false);
    await onUpload(slot.key, file);
    e.target.value = '';
  };

  const isRef = slot.type === 'reference';

  if (url) {
    return (
      <div className="relative" data-testid={`photo-slot-${slot.key}`}>
        <div className={`relative w-full aspect-square rounded-xl overflow-hidden border-2 ${isRef ? 'border-blue-200' : 'border-cream-100'}`}>
          <img src={url} alt="" className="w-full h-full object-cover" />
          {isRef && (
            <div className="absolute bottom-0 inset-x-0 bg-blue-600/80 backdrop-blur-sm py-1 text-center">
              <span className="text-[10px] font-body font-semibold text-white tracking-wide uppercase">Reference Image</span>
            </div>
          )}
          <button type="button" onClick={() => onRemove(slot.key)}
            className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 shadow-sm hover:bg-white transition-all"
            data-testid={`remove-photo-${slot.key}`}>
            <X size={12} className="text-[#2C2A29]" />
          </button>
        </div>
        <p className="text-[10px] font-body text-[#A89F91] text-center mt-1">
          {slot.label} {slot.required && <span className="text-red-400">*</span>}
        </p>
      </div>
    );
  }

  const hasError = photoError && slot.required && !url;

  return (
    <>
      <div data-testid={`photo-slot-${slot.key}`}>
        <button type="button" onClick={() => setDrawerOpen(true)}
          className={`w-full aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all
            ${hasError ? 'border-red-300 bg-red-50/50' : isRef ? 'border-blue-200 hover:border-blue-300 hover:bg-blue-50/30' : 'border-cream-200 hover:border-[#F4C2C2] hover:bg-[#FFF5F5]'}`}
          data-testid={`add-photo-${slot.key}`}>
          {uploading === slot.key ? (
            <div className="w-5 h-5 border-2 border-blush-200 border-t-blush-400 rounded-full animate-spin" />
          ) : isRef ? (
            <>
              <ImageIcon size={18} className="text-blue-400" />
              <span className="text-[10px] text-blue-400 font-body font-medium">Reference</span>
            </>
          ) : (
            <>
              <Camera size={18} className={hasError ? 'text-red-400' : 'text-[#A89F91]'} />
              <span className={`text-[10px] font-body font-medium ${hasError ? 'text-red-400' : 'text-[#A89F91]'}`}>
                {slot.required ? 'Required' : 'Optional'}
              </span>
            </>
          )}
        </button>
        <p className={`text-[10px] font-body text-center mt-1 ${hasError ? 'text-red-400' : 'text-[#A89F91]'}`}>
          {slot.label} {slot.required && <span className="text-red-400">*</span>}
        </p>
      </div>

      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="rounded-t-3xl border-cream-100 bg-white">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="font-heading text-lg text-[#2C2A29]">
              {isRef ? 'Add Reference Image' : `Add ${slot.label}`}
            </DrawerTitle>
            <p className="text-xs font-body text-[#78716C]">
              {isRef ? 'Brand or retailer image only. This will be labeled as Reference Image.' : 'Show the real item, condition, label, or purchase receipt.'}
            </p>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-2">
            <button type="button"
              onClick={() => cameraRef.current?.click()}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-cream-50 hover:bg-cream-100 transition-all"
              data-testid={`take-photo-${slot.key}`}>
              <Camera size={20} className="text-[#C5A880]" />
              <span className="font-body font-semibold text-sm text-[#2C2A29]">Take Photo</span>
            </button>
            <button type="button"
              onClick={() => galleryRef.current?.click()}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-cream-50 hover:bg-cream-100 transition-all"
              data-testid={`choose-gallery-${slot.key}`}>
              <ImagePlus size={20} className="text-[#C5A880]" />
              <span className="font-body font-semibold text-sm text-[#2C2A29]">Choose from Gallery</span>
            </button>
            <button type="button" onClick={() => setDrawerOpen(false)}
              className="w-full text-center py-2.5 text-sm font-body text-[#78716C] hover:text-[#2C2A29]">
              Cancel
            </button>
          </div>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
          <input ref={galleryRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
        </DrawerContent>
      </Drawer>
    </>
  );
}

export default function EditListingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    brand: '', product_name: '', description: '', category: '', condition: '',
    remaining_amount: '', expiry_date: '', price: '',
    size_value: '', size_unit: '',
    opening_date: '', pao_months: '',
    selling_reason: '', selling_reason_text: '',
  });
  const [photoSlots, setPhotoSlots] = useState({});
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [photoError, setPhotoError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/listings/${id}`)
      .then(r => {
        const l = r.data;
        if (user && l.user_id !== user.user_id) {
          toast.error('Not authorized to edit this listing');
          navigate('/');
          return;
        }
        setForm({
          brand: l.brand || '',
          product_name: l.product_name || '',
          description: l.description || '',
          category: l.category || '',
          condition: l.condition || '',
          remaining_amount: l.remaining_amount || '',
          expiry_date: l.expiry_date || '',
          price: l.price?.toString() || '',
          size_value: l.size_value || '',
          size_unit: l.size_unit || '',
          opening_date: l.opening_date || '',
          pao_months: l.pao_months ? String(l.pao_months) : '',
          selling_reason: l.selling_reason || '',
          selling_reason_text: l.selling_reason_text || '',
        });
        // Map existing photos into slots
        const slotKeys = ['actual_1', 'actual_2', 'actual_3', 'actual_4'];
        const mapped = {};
        (l.photos || []).forEach((url, i) => {
          if (i < slotKeys.length) mapped[slotKeys[i]] = url;
        });
        if (l.reference_photo) mapped.reference = l.reference_photo;
        setPhotoSlots(mapped);
      })
      .catch(() => { toast.error('Listing not found'); navigate('/'); })
      .finally(() => setLoading(false));
  }, [id, user, navigate]);

  const handleSlotUpload = async (slotKey, file) => {
    setUploadingSlot(slotKey);
    setPhotoError(false);
    try {
      const compressed = await compressListingPhoto(file);
      const formData = new FormData();
      formData.append('file', compressed);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setPhotoSlots(prev => ({ ...prev, [slotKey]: url }));
    } catch (err) {
      toast.error(err.message || 'Failed to upload photo');
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleSlotRemove = (slotKey) => {
    setPhotoSlots(prev => {
      const next = { ...prev };
      delete next[slotKey];
      return next;
    });
  };

  const getActualPhotos = () => {
    return ['actual_1', 'actual_2', 'actual_3', 'actual_4']
      .map(k => photoSlots[k])
      .filter(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const actualPhotos = getActualPhotos();
    if (actualPhotos.length < 3) {
      setPhotoError(true);
      toast.error('Please upload at least 3 actual product photos');
      return;
    }
    setSubmitting(true);
    try {
      // Client-side: opening date required for non-sealed conditions, must be today or earlier.
      const isSealed = (form.condition || '').toLowerCase().includes('new/sealed');
      if (!isSealed && !form.opening_date) {
        toast.error('Opening date is required for opened/used items.');
        setSubmitting(false);
        return;
      }
      if (form.opening_date) {
        const todayWIB = new Date(Date.now() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);
        if (form.opening_date > todayWIB) {
          toast.error('Opening date cannot be in the future.');
          setSubmitting(false);
          return;
        }
      }
      // At least one of Expiry Date or PAO must be filled.
      if (!form.expiry_date && !form.pao_months) {
        toast.error('Please fill either Expiry Date or PAO — buyers need at least one.');
        setSubmitting(false);
        return;
      }
      const payload = {
        ...form,
        photos: actualPhotos,
        reference_photo: photoSlots.reference || null,
        price: parseFloat(form.price),
        quantity: 1,
        currency: 'IDR',
        size_value: form.size_value || null,
        size_unit: form.size_unit || null,
        opening_date: isSealed ? null : (form.opening_date || null),
        pao_months: form.pao_months ? parseInt(form.pao_months, 10) : null,
      };
      await axios.put(`${API}/listings/${id}`, payload);
      toast.success('Listing updated!');
      navigate(`/listing/${id}`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update listing');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFAF8]"><Header />
        <div className="max-w-2xl mx-auto px-4 py-8 animate-pulse">
          <div className="h-8 bg-cream-100 rounded w-48 mb-6" />
          <div className="h-96 bg-cream-100 rounded-3xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-body text-[#78716C] hover:text-[#2C2A29] mb-4" data-testid="edit-back">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] mb-2" data-testid="edit-listing-title">Edit Listing</h1>
          <p className="font-body text-[#78716C]">Update your product details</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-cream-100/50">
          {/* Photo Upload Slots */}
          <div data-testid="photo-upload-section">
            <label className="block text-sm font-body font-medium text-[#2C2A29] mb-1">Photos</label>
            <p className="text-xs font-body text-[#78716C] mb-3">
              Upload at least 3 photos of the actual item. You may add 1 optional reference image.
            </p>
            {photoError && (
              <div className="flex items-center gap-1.5 mb-3 text-red-500" data-testid="photo-validation-error">
                <AlertCircle size={13} />
                <p className="text-xs font-body font-medium">Please upload at least 3 actual product photos</p>
              </div>
            )}
            <div className="grid grid-cols-5 gap-2 sm:gap-3">
              {PHOTO_SLOTS.map(slot => (
                <PhotoSlot
                  key={slot.key}
                  slot={slot}
                  url={photoSlots[slot.key]}
                  onUpload={handleSlotUpload}
                  onRemove={handleSlotRemove}
                  uploading={uploadingSlot}
                  photoError={photoError}
                />
              ))}
            </div>
            <div className="flex gap-4 mt-2">
              <p className="text-xs font-body text-[#A89F91]">
                <span className="text-[#2C2A29] font-medium">Photo 1-4:</span> Show the real item, condition, label, or purchase receipt.
              </p>
              <p className="text-xs font-body text-blue-400">
                <span className="text-blue-500 font-medium">Reference:</span> Brand or retailer image only.
              </p>
            </div>
          </div>

          {/* Brand & Product Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Brand *</label>
              <input type="text" required value={form.brand}
                onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                placeholder="e.g., Laneige" data-testid="input-brand" />
            </div>
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Product Name *</label>
              <input type="text" required value={form.product_name}
                onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                placeholder="e.g., Water Sleeping Mask" data-testid="input-product-name" />
            </div>
          </div>

          {/* Category & Condition */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Description */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Description</label>
              <textarea value={form.description} maxLength={300}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent resize-none"
                rows={3} placeholder="Describe your product..." data-testid="input-description" />
              <p className={`text-xs font-body mt-1 text-right ${form.description.length >= 280 ? 'text-[#E05A5A]' : 'text-[#A89F91]'}`}>
                {form.description.length}/300
              </p>
            </div>
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Category *</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="bg-cream-50 border-cream-100 rounded-xl h-12" data-testid="select-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Condition *</label>
              <Select value={form.condition} onValueChange={v => setForm(f => {
                const next = { ...f, condition: v, remaining_amount: v };
                if ((v || '').toLowerCase().includes('new/sealed')) {
                  next.opening_date = '';
                }
                return next;
              })}>
                <SelectTrigger className="bg-cream-50 border-cream-100 rounded-xl h-12" data-testid="select-condition">
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Expiry & Size */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Expiry Date <span className="text-xs font-normal text-[#A89F91]">(or fill PAO below)</span></label>
              <input type="text" value={form.expiry_date}
                onChange={e => {
                  let val = e.target.value.replace(/[^\d/]/g, '');
                  if (val.length === 2 && !val.includes('/') && form.expiry_date.length < val.length) {
                    val = val + '/';
                  }
                  if (val.length <= 7) setForm(f => ({ ...f, expiry_date: val }));
                }}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                placeholder="MM/YYYY" maxLength={7} data-testid="input-expiry" />
            </div>
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Size</label>
              <div className="flex gap-2">
                <input type="text" value={form.size_value}
                  onChange={e => setForm(f => ({ ...f, size_value: e.target.value }))}
                  className="flex-1 min-w-0 bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                  placeholder="e.g. 50" data-testid="input-size-value" />
                <Select value={form.size_unit} onValueChange={v => setForm(f => ({ ...f, size_unit: v }))}>
                  <SelectTrigger className="w-[90px] flex-shrink-0 bg-cream-50 border-cream-100 rounded-xl h-12" data-testid="select-size-unit">
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {SIZE_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Opening Date & PAO */}
          {(() => {
            const isSealed = (form.condition || '').toLowerCase().includes('new/sealed');
            const todayWIB = new Date(Date.now() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">
                    Opening Date{!isSealed && ' *'}
                    {isSealed && <span className="ml-1 text-xs font-normal text-[#A89F91]">(not needed for sealed items)</span>}
                  </label>
                  <input
                    type="date"
                    value={isSealed ? '' : form.opening_date}
                    max={todayWIB}
                    disabled={isSealed}
                    required={!isSealed}
                    onChange={e => setForm(f => ({ ...f, opening_date: e.target.value }))}
                    className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent disabled:bg-cream-100/60 disabled:text-[#A89F91] disabled:cursor-not-allowed"
                    data-testid="input-opening-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">
                    PAO <span className="text-xs font-normal text-[#A89F91]">(Period After Opening — fill if no expiry date)</span>
                  </label>
                  <Select value={form.pao_months} onValueChange={v => setForm(f => ({ ...f, pao_months: v === '__none__' ? '' : v }))}>
                    <SelectTrigger className="bg-cream-50 border-cream-100 rounded-xl h-12" data-testid="select-pao-months">
                      <SelectValue placeholder="Select PAO" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None / Clear</SelectItem>
                      <SelectItem value="6">6 months</SelectItem>
                      <SelectItem value="12">12 months</SelectItem>
                      <SelectItem value="24">24 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()}

          {/* Price & Selling Reason */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Price (IDR) *</label>
              <input type="number" required value={form.price} min="0"
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                placeholder="150000" data-testid="input-price" />
              {(() => {
                const p = parseInt(form.price, 10);
                if (!p || p <= 0) return null;
                const fee = Math.round(p * 0.05);
                const net = p - fee;
                return (
                  <p className="text-xs font-body text-[#78716C] mt-1.5" data-testid="payout-preview">
                    You'll receive <span className="font-semibold text-emerald-700">Rp {net.toLocaleString('id-ID')}</span>{' '}
                    <span className="text-[#A89F91]">(after 5% platform fee · Rp {fee.toLocaleString('id-ID')})</span>
                  </p>
                );
              })()}
            </div>
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Why are you selling it? *</label>
              <Select value={form.selling_reason} onValueChange={v => setForm(f => ({ ...f, selling_reason: v }))}>
                <SelectTrigger className="bg-cream-50 border-cream-100 rounded-xl h-12" data-testid="select-selling-reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {SELLING_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.selling_reason === 'Other' && (
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Tell us more</label>
              <textarea value={form.selling_reason_text}
                onChange={e => setForm(f => ({ ...f, selling_reason_text: e.target.value }))}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent resize-none"
                rows={3} placeholder="Optional details..." data-testid="input-selling-reason-text" />
            </div>
          )}

          <button type="submit" disabled={submitting || !!uploadingSlot}
            className="w-full bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-3.5 font-body font-semibold transition-all hover:bg-[#ECAEAE] hover:shadow-md disabled:opacity-50"
            data-testid="update-listing-btn">
            {submitting ? 'Updating...' : 'Update Listing'}
          </button>
        </form>
      </main>
    </div>
  );
}
